import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import axiosConn from "config/axiosConn";
import SpinnerOverlay from "../Components/SchoolAdmin/Spinner";
import Helper from "../helper/Common_Helper";

const PAGE_SIZE_OPTIONS = [12, 24, 36, 54];

// district is stored as a bare numeric id on some records; hide those
const cleanPlace = (district, state) =>
  [/^\d+$/.test(String(district || "").trim()) ? "" : district, state]
    .filter(Boolean)
    .join(", ");

const ACTIVITY_LABELS = {
  awareness: "Awareness Campaign",
  sitevisit: "Site Visit",
  recharge: "Water Recharge Activity",
};

const activityLabel = (type) =>
  String(type || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => ACTIVITY_LABELS[t] || t)
    .join(", ");

function RasImageGallery() {
  const [mediaItems, setMediaItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [loading, setLoading] = useState(false);
  const [failedUrls, setFailedUrls] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const getRasGallery = async (page = 1, limit = pageSize) => {
    try {
      setLoading(true);
      const response = await axiosConn("post", "get-ras-gallery", {
        page,
        limit,
      });
      if (response.data.status === "success") {
        setMediaItems(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || page);
        setFailedUrls([]);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRasGallery(1, pageSize);
  }, []);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages && page !== currentPage && !loading) {
      getRasGallery(page, pageSize);
    }
  };

  const handlePageSizeChange = (size) => {
    const limit = parseInt(size) || 12;
    setPageSize(limit);
    getRasGallery(1, limit);
  };

  // hide any media the browser fails to load so no blank tiles are shown
  const handleMediaError = (url) => {
    setFailedUrls((prev) => (prev.includes(url) ? prev : [...prev, url]));
  };

  const visibleMedia = mediaItems.filter(
    (item) => item.file_url && !failedUrls.includes(item.file_url)
  );

  const pageNumbers = () => {
    const windowSize = 5;
    let start = Math.max(1, currentPage - Math.floor(windowSize / 2));
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  };

  return (
    <>
      {loading && <SpinnerOverlay />}
      <section className="sevenScopeSec eventGallerySec">
        <div className="container-fluid">
          <div className="eventGalleryHead">
            <h2 className="text-center">
              RAS <span className="hdngBrkr">Image Gallery</span>
            </h2>
            <p className="text-center galleryIntro">
              Images and videos from Rain-water Augmentation &amp; Storage (RAS)
              activities carried out by Eco Clubs across the country
            </p>
          </div>

          <div className="eventGalleryToolbar">
            <div className="eventGalleryCount">
              {totalPages > 0 && (
                <>
                  Page {currentPage.toLocaleString("en-IN")} of{" "}
                  {totalPages.toLocaleString("en-IN")}
                </>
              )}
            </div>
            <div className="eventGalleryPageSize">
              <label htmlFor="rasGallerySize">Items per page</label>
              <select
                id="rasGallerySize"
                value={pageSize}
                disabled={loading}
                onChange={(e) => handlePageSizeChange(e.target.value)}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="eventGalleryGrid">
            {visibleMedia.length > 0 ? (
              visibleMedia.map((item, i) => (
                <div
                  className="eventGalleryCard"
                  key={`${item.file_url}-${i}`}
                  onClick={() => setLightbox(item)}
                >
                  <div className="eventGalleryImgWrap">
                    {item.media_type === "video" ? (
                      <>
                        <video
                          className="rasGalleryVideoTile"
                          preload="metadata"
                          muted
                          onError={() => handleMediaError(item.file_url)}
                        >
                          <source src={item.file_url} />
                        </video>
                        <span className="rasGalleryPlayBadge">&#9658;</span>
                      </>
                    ) : (
                      <img
                        src={item.file_url}
                        alt={item.school_name || "RAS activity"}
                        loading="lazy"
                        onError={() => handleMediaError(item.file_url)}
                      />
                    )}
                    {activityLabel(item.activity_type) && (
                      <span className="eventGalleryThemeBadge">
                        {activityLabel(item.activity_type)}
                      </span>
                    )}
                  </div>
                  <div className="eventGalleryCaption">
                    <strong title={item.school_name}>{item.school_name}</strong>
                    <span>{cleanPlace(item.district, item.state)}</span>
                    {item.submittedAt && (
                      <span className="eventGalleryDate">
                        Submitted: {Helper.formatDate(item.submittedAt)}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              !loading && (
                <p className="text-center w-100">No RAS activity media found.</p>
              )
            )}
          </div>

          {totalPages > 1 && (
            <div className="eventGalleryPagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
              >
                &laquo; Previous
              </button>
              {pageNumbers().map((p) => (
                <button
                  key={p}
                  className={p === currentPage ? "active" : ""}
                  onClick={() => handlePageChange(p)}
                  disabled={loading}
                >
                  {p.toLocaleString("en-IN")}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
              >
                Next &raquo;
              </button>
            </div>
          )}
        </div>
      </section>

      <Modal
        show={!!lightbox}
        onHide={() => setLightbox(null)}
        size="lg"
        centered
      >
        <span onClick={() => setLightbox(null)} className="closebtn">
          X
        </span>
        <Modal.Body style={{ padding: 20 }}>
          {lightbox && (
            <>
              {lightbox.media_type === "video" ? (
                <video
                  controls
                  autoPlay
                  style={{ width: "100%", borderRadius: 8 }}
                  onError={() => {
                    handleMediaError(lightbox.file_url);
                    setLightbox(null);
                  }}
                >
                  <source src={lightbox.file_url} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={lightbox.file_url}
                  alt={lightbox.school_name || "RAS activity"}
                  style={{ width: "100%", borderRadius: 8 }}
                  onError={() => {
                    handleMediaError(lightbox.file_url);
                    setLightbox(null);
                  }}
                />
              )}
              <div className="eventGalleryLightboxMeta">
                <strong>{lightbox.school_name}</strong>
                <div>{activityLabel(lightbox.activity_type)}</div>
                <div>{cleanPlace(lightbox.district, lightbox.state)}</div>
                <div>
                  {lightbox.submittedAt
                    ? `Submitted: ${Helper.formatDate(lightbox.submittedAt)}`
                    : ""}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default RasImageGallery;
